import React, { Suspense } from "react";
import ProtectedRoute from "ProtectedRoute";

const ControlCenter = React.lazy(() => import("fixedops/pages/ControlCenter"));
const CommandCenter = React.lazy(() => import("fixedops/pages/CommandCenter"));
const Workflows = React.lazy(() => import("fixedops/pages/Workflows"));
const Invoices = React.lazy(() => import("fixedops/pages/Invoices"));
const InventoryItems = React.lazy(() => import("fixedops/pages/Inventory/Items"));
const InventoryLocations = React.lazy(() => import("fixedops/pages/Inventory/Locations"));
const InventoryOverview = React.lazy(() => import("fixedops/pages/Inventory/Inventory"));
const SoftwareCatalogSubscriptions = React.lazy(() =>
  import("fixedops/pages/SoftwareCatalog/Subscriptions")
);

const Lazy = ({ children }) => (
  <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
    {children}
  </Suspense>
);

// Phase A scaffold — the Control Center / Command Center / Workflows
// landing pages. The other sidebar items (work orders, tickets, quotations,
// payables, inventory, approvals inbox, client assets, receivables,
// service catalog, settings sub-pages) keep pointing at their existing
// flex2 routes registered in src/routes.js until per-page Fixed Ops
// surfaces land. appMode: "flex2" so these are part of the Fixed
// Operations system from the picker's perspective.
// type:"hidden" + no `name` keeps these out of the MAIN <Sidenav>'s
// menu filter (which renders any route with a `name`). The pages are
// still reachable by direct URL + the FixedOpsPortalShell renders
// its own sidebar. Leaving `name` on caused FixedOps entries to leak
// into the login page sidebar for every non-FixedOps user.
const fixedOpsRoutes = [
  {
    key: "fixedops-control-center",
    route: "/fixedops/control-center",
    component: <ProtectedRoute component={<Lazy><ControlCenter /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    key: "fixedops-command-center",
    route: "/fixedops/command-center",
    component: <ProtectedRoute component={<Lazy><CommandCenter /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    // Invoices the org has issued. Replaces /receivables as the menu target —
    // same underlying query (creatorOrganizationId = caller), but with the
    // actions a vendor needs on a bill they raised. /receivables stays routed
    // for bookmarks.
    key: "fixedops-invoices",
    route: "/fixedops/invoices",
    component: <ProtectedRoute component={<Lazy><Invoices /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    key: "fixedops-settings-workflows",
    route: "/fixedops/settings/workflows",
    component: <ProtectedRoute component={<Lazy><Workflows /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    key: "fixedops-inventory-items",
    route: "/fixedops/inventory/items",
    component: <ProtectedRoute component={<Lazy><InventoryItems /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    key: "fixedops-inventory-locations",
    route: "/fixedops/inventory/locations",
    component: <ProtectedRoute component={<Lazy><InventoryLocations /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    key: "fixedops-inventory-overview",
    route: "/fixedops/inventory/inventory",
    component: <ProtectedRoute component={<Lazy><InventoryOverview /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
  {
    // Software Catalog → Subscriptions. Visible in the sidebar only for an org
    // carrying the "Software & Subscriptions" vendor type (requiresVendorType
    // in FixedOpsPortalNavigation.js); the route itself is reachable by URL for
    // anyone whose ABAC lets them read Subscription, which the endpoint
    // enforces server-side.
    key: "fixedops-software-catalog-subscriptions",
    route: "/fixedops/software-catalog/subscriptions",
    component: <ProtectedRoute component={<Lazy><SoftwareCatalogSubscriptions /></Lazy>} />,
    appMode: "flex2",
    type: "hidden",
  },
];

export default fixedOpsRoutes;
